import type { ReactNode } from 'react';
import { Input, Switch } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { CreateEventJobRequest } from '@/api/media/live-event-jobs-types';
import { FormSectionCard } from './FormSectionCard';
import styles from './EventJobCreate.module.css';

interface BasicSectionProps {
  form: CreateEventJobRequest;
  errors: Partial<Record<'app_name' | 'stream_name', string>>;
  onChange: (field: keyof CreateEventJobRequest, value: unknown) => void;
  clearError: (field: string) => void;
  /** Extra flag switches appended to the switch row (linear wizard only). */
  extraFlags?: ReactNode;
}

/** App name, stream name, and feature flag toggles. */
export function BasicSection({ form, errors, onChange, clearError, extraFlags }: BasicSectionProps) {
  return (
    <FormSectionCard title="Basic Information" description="App name, stream name, and optional feature flags.">
      <div className={styles.grid2}>
        <Field label="App Name" required error={errors.app_name} description="Only letters, digits, and underscores — max 64 characters.">
          {(fp) => (
            <Input
              {...fp}
              value={form.app_name}
              onChange={(e) => { onChange('app_name', e.target.value); clearError('app_name'); }}
              placeholder="event_app"
            />
          )}
        </Field>
        <Field label="Stream Name" required error={errors.stream_name} description="Only letters, digits, and underscores — max 64 characters.">
          {(fp) => (
            <Input
              {...fp}
              value={form.stream_name}
              onChange={(e) => { onChange('stream_name', e.target.value); clearError('stream_name'); }}
              placeholder="live_01"
            />
          )}
        </Field>
      </div>

      <div className={styles.switchRow}>
        <label className={styles.switchItem}>
          <Switch
            checked={form.timeshift_enabled}
            onCheckedChange={(v) => onChange('timeshift_enabled', v)}
            id="timeshift"
          />
          <span>Timeshift (Seek Back)</span>
        </label>
        <label className={styles.switchItem}>
          <Switch
            checked={form.gpu_enabled}
            onCheckedChange={(v) => onChange('gpu_enabled', v)}
            id="gpu"
          />
          <span>GPU Encoding</span>
        </label>
        {extraFlags}
      </div>
    </FormSectionCard>
  );
}
